import { z } from 'zod';

export const rewardPair = z.object({ exp: z.number().int().nonnegative(), coins: z.number().int().nonnegative() });
const amount = z.number().int().nonnegative();
export const soloResources = z.object({
  coins: amount,
  exp: amount,
  lives: amount,
  maxLives: amount.positive(),
  nextLifeAt: z.string().nullish(),
  inventory: z.object({ fiftyFifty: amount, freeze: amount, swapFrame: amount }),
});
export const playQuestion = z.object({
  id: z.string().min(1),
  difficulty: z.number().int().min(1).max(5).optional(),
  kind: z.string(),
  type: z.literal('choice'),
  optionTitleIds: z.tuple([z.string(), z.string(), z.string(), z.string()]),
  screenshots: z.array(z.string()).min(1),
});
export const packPlay = z
  .object({
    packId: z.string(),
    accessSource: z.enum(['free', 'purchased', 'premium', 'locked']),
    currentQuestionId: z.string().optional(),
    solvedQuestionIds: z
      .array(z.string())
      .nullish()
      .transform((ids) => ids ?? []),
    totalQuestions: amount.positive(),
    status: z.string(),
    earnedRewards: rewardPair,
    totalRewards: rewardPair,
  })
  .refine((p) => p.solvedQuestionIds.length <= p.totalQuestions);
export const packAnswer = z.object({
  correct: z.boolean(),
  currentQuestionId: z.string().optional(),
  solvedQuestionIds: z
    .array(z.string())
    .nullish()
    .transform((ids) => ids ?? []),
  totalQuestions: amount.positive(),
  status: z.string(),
  earnedRewards: rewardPair,
  totalRewards: rewardPair,
  rewards: rewardPair,
  resources: soloResources,
});
export const endlessRun = z.object({
  kind: z.enum(['film', 'series', 'game', 'anime', '']).optional(),
  correctAnswers: amount,
  correctStreak: amount,
  roundsCompleted: amount,
  status: z.string(),
  earnedRewards: rewardPair,
});
export const endlessSnapshot = z.object({
  kind: z.string().optional(),
  correct: z.boolean().optional(),
  correctTitleId: z.string().optional(),
  question: playQuestion.optional(),
  run: endlessRun.optional(),
  dailyEarnedExp: amount,
  dailyExpCap: amount,
  dailyResetAt: z.string(),
  earnedRewards: rewardPair,
  bestCorrectStreak: amount,
  bestDistance: amount,
  categories: z.record(z.string(), z.object({ bestDistance: amount, bestCorrectStreak: amount })).optional(),
});
export const endlessStart = z.object({ question: playQuestion.optional(), run: endlessRun.optional() });
export const endlessAnswer = z
  .object({
    correct: z.boolean(),
    correctTitleId: z.string().optional(),
    finished: z.boolean().optional(),
    question: playQuestion.optional(),
    run: endlessRun.optional(),
    dailyEarnedExp: amount,
    dailyExpCap: amount,
    dailyResetAt: z.string(),
    rewards: rewardPair,
    resources: soloResources,
  })
  .transform((state) => ({
    ...state,
    finished: state.finished === true || state.run?.status === 'finished' || state.resources.lives === 0,
  }));
export const soloHint = z.object({
  hint: z.string(),
  removedOptionTitleIds: z.array(z.string()).optional(),
  screenshot: z.string().optional(),
  resources: soloResources,
});

export const endlessAbort = z.object({
  finished: z.literal(true),
  lifeSpent: z.boolean(),
  run: endlessRun.optional(),
  resources: soloResources.optional(),
});
